import { AllPermissions, Permission } from '@employer/app/config/permission.config'
import { CustomerPermissions } from '@engineering11/customer-web'
import { Observable } from 'rxjs'
import { CnectFeature, JobAutomationPermissions } from 'shared-lib'

export type SettingsGroup = 'Company' | 'Website' | 'People' | 'Settings' | 'Openings' | 'Pipelines'

export interface SettingsCategory {
  id: string
  label: string
  icon: string
  route: string
  group: SettingsGroup
  /** AND-combined; each entry must equal the route's EFFECTIVE guard (parent AND child), NOT the nav leaf. */
  permissions?: AllPermissions[]
  feature?: CnectFeature
  /** Only set for categories with a verified, reachable in-page anchor (Task 13). Absent => page-level navigation. */
  section?: string
  /**
   * One-line subtitle shown under the label on the hub card (CNCT-6779). Kept short
   * enough to hold one line at three-column width; also searchable.
   */
  description: string
  /**
   * CNCT-6779: makes the entry favoritable from the hub grid and the Settings rail,
   * writing the same `employer_nav_favorites` list the left-nav stars write. These
   * are the ids the former Company nav leaves used, so favourites already persisted
   * against them survive the nav being condensed — `nav-favorites-section` resolves
   * an id through the catalog when it is no longer in the nav tree.
   */
  favoriteId: string
  /** Label used in the Favorites section, where the entry sits out of its group's context. */
  secondaryName?: string
  searchKeywords: string[]
}

/** A catalog entry paired with its resolved visibility gate (route's effective guard). */
export type GatedSettingsCategory = SettingsCategory & { isVisible$: Observable<boolean> }

/**
 * Hub-only source of truth for the All Company Settings hub. This const drives the
 * settings rail, overview grid, and keyword search — it is NEVER wired into the
 * left-nav tree (`navigation.service.navigationItems$`).
 *
 * CNCT-6779 made this the ONLY route to company configuration: the expandable Company
 * nav was condensed to a single leaf pointing here, so every former child needs an
 * entry. Seven were added in that change (Profile, Marketing Channels, Pipeline
 * Configuration, AI Chat Support, Organization Access, Team, Org Charts), each
 * carrying the same `feature` flag and permission its nav leaf carried. Entry ORDER
 * is meaningful — the overview grid renders in catalog order, and the rail groups it
 * by `group` in `GROUP_ORDER` — so both mirror the old nav's ordering. Each entry's
 * `permissions`/`feature` encode the destination route's EFFECTIVE guard
 * (parent `canActivate` AND child guards per `customer.routing.ts`/`users.routing.ts`/
 * `customer-clients.routing.ts`), which is the security-correct value and may differ
 * from the corresponding nav leaf's gate (intentional — see the tech spec).
 *
 * `route` values are literal strings mirroring `ROUTES` (importing `ROUTES` here would
 * create a circular dependency with `navigation.service`); a drift test asserts they
 * stay in sync with `ROUTES`.
 */
export const SETTINGS_CATALOG: SettingsCategory[] = [
  {
    id: 'customer',
    favoriteId: 'company-personnel-account',
    secondaryName: 'Company Account',
    label: 'Company Account',
    icon: 'business',
    route: '/settings/company-account',
    group: 'Company',
    description: 'Legal name and industry',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['account', 'company', 'organization', 'business', 'profile'],
  },
  {
    id: 'profile',
    favoriteId: 'company-profile',
    secondaryName: 'Company Profile',
    label: 'Profile',
    icon: 'corporate_fare',
    route: '/settings/company-profile',
    group: 'Company',
    description: 'Company identity and public profile',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerCompanyProfile',
    searchKeywords: ['profile', 'identity', 'public', 'company', 'about', 'brand'],
  },
  {
    id: 'departments',
    favoriteId: 'company-departments',
    secondaryName: 'Company Departments',
    label: 'Departments',
    icon: 'account_tree',
    route: '/settings/departments',
    group: 'Company',
    description: 'Group teams and openings',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['departments', 'teams', 'divisions', 'org'],
  },
  {
    id: 'locations',
    favoriteId: 'company-locations',
    secondaryName: 'Company Locations',
    label: 'Locations',
    icon: 'location_on',
    route: '/settings/locations',
    group: 'Company',
    description: 'Offices and job sites',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['address', 'office', 'sites', 'branch', 'locations'],
  },
  {
    id: 'marketing-channels',
    favoriteId: 'company-marketing-channels',
    label: 'Marketing Channels',
    icon: 'cell_tower',
    route: '/settings/marketing-channels',
    group: 'Company',
    description: 'Connect Meta, Google, LinkedIn',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerMarketingChannels',
    searchKeywords: ['marketing', 'channels', 'meta', 'facebook', 'instagram', 'google', 'linkedin', 'connect', 'distribution'],
  },
  {
    id: 'content',
    favoriteId: 'company-website-site-content',
    secondaryName: 'Website Content',
    label: 'Site Content',
    icon: 'article',
    route: '/settings/content',
    group: 'Website',
    description: 'Page copy and sections',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['content', 'careers', 'page', 'website', 'copy', 'about'],
  },
  {
    id: 'logo',
    favoriteId: 'company-website-logo-and-video',
    secondaryName: 'Website Logo and Video',
    label: 'Logo & Video',
    icon: 'image',
    route: '/settings/logo',
    group: 'Website',
    description: 'Brand imagery and intro video',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['logo', 'video', 'branding', 'brand', 'image'],
  },
  {
    id: 'hero',
    favoriteId: 'company-website-hero-section',
    secondaryName: 'Website Hero Section',
    label: 'Hero Section',
    icon: 'wallpaper',
    route: '/settings/hero',
    group: 'Website',
    description: 'Careers page banner',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['hero', 'banner', 'header', 'careers', 'page'],
  },
  {
    id: 'social',
    favoriteId: 'company-website-social-media',
    secondaryName: 'Website Social Media',
    label: 'Social Media',
    icon: 'share',
    route: '/settings/social',
    group: 'Website',
    description: 'Profile links on your careers page',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['social', 'linkedin', 'facebook', 'twitter', 'instagram', 'links'],
  },
  {
    id: 'interview-questions',
    favoriteId: 'engage-settings-question-library',
    secondaryName: 'Interview Question Library',
    label: 'Question Library',
    icon: 'quiz',
    route: '/settings/interview-questions',
    group: 'Pipelines',
    description: 'Reusable screening questions',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerInterviewQuestions',
    searchKeywords: ['questions', 'interview', 'library', 'screening', 'prompts'],
  },
  {
    id: 'pipeline-config',
    favoriteId: 'engage-settings-pipeline-config',
    label: 'Pipeline Configuration',
    icon: 'view_kanban',
    route: '/engagement/settings/pipeline',
    group: 'Pipelines',
    description: 'Stages applicants move through',
    permissions: [Permission.ViewCandidatePool],
    feature: 'employerPipelineConfig',
    searchKeywords: ['pipeline', 'stages', 'configuration', 'workflow', 'hiring', 'steps', 'statuses'],
  },
  {
    id: 'automation-settings',
    favoriteId: 'engage-settings-automation',
    label: 'Automation Settings',
    icon: 'smart_toy',
    route: '/settings/automation-settings',
    group: 'Pipelines',
    description: 'Automatic applicant actions',
    permissions: [Permission.ManageCompanySettings, JobAutomationPermissions.ManageAutomation],
    feature: 'employerJobAutomation',
    searchKeywords: ['automation', 'workflow', 'triggers', 'messages', 'auto'],
  },
  {
    id: 'job-application-settings',
    favoriteId: 'openings-job-application-settings',
    label: 'Opening & Application Settings',
    icon: 'tune',
    route: '/settings/job-application-settings',
    group: 'Openings',
    description: 'Defaults for new openings',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerJobApplicationSettings',
    searchKeywords: ['job', 'application', 'apply', 'settings', 'openings', 'forms'],
  },
  {
    id: 'compliance',
    favoriteId: 'company-settings-compliance',
    secondaryName: 'Company Compliance',
    label: 'Compliance',
    icon: 'gavel',
    route: '/settings/compliance',
    group: 'Settings',
    description: 'EEO and legal disclaimers',
    permissions: [Permission.ManageCompanySettings],
    searchKeywords: ['compliance', 'legal', 'disclaimer', 'eeo', 'policy', 'regulation'],
  },
  {
    id: 'ai-chat',
    favoriteId: 'company-settings-ai-chat',
    label: 'AI Chat Support',
    icon: 'support_agent',
    route: '/settings/ai-chat',
    group: 'Settings',
    description: 'Automated chat assistance behavior',
    feature: 'employerAiChatSupport',
    searchKeywords: ['ai', 'chat', 'support', 'assistant', 'bot', 'automated', 'help', 'conversation'],
  },
  {
    id: 'org-access',
    favoriteId: 'company-personnel-org-access',
    label: 'Organization Access',
    icon: 'admin_panel_settings',
    route: '/settings/personnel/access',
    group: 'People',
    description: 'Users, companies, and roles matrix',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerOrgAccess',
    searchKeywords: ['organization', 'access', 'org', 'admin', 'roles', 'companies', 'matrix', 'permissions'],
  },
  {
    id: 'users',
    favoriteId: 'company-personnel-users',
    secondaryName: 'Company Users',
    label: 'Manage Users',
    icon: 'group',
    route: '/users',
    group: 'People',
    description: 'Teammates and permissions',
    permissions: [Permission.ManageAllCustomerUsers],
    searchKeywords: ['users', 'team', 'members', 'people', 'roles', 'permissions', 'invite'],
  },
  {
    id: 'team',
    favoriteId: 'company-personnel-team',
    secondaryName: 'Company Team',
    label: 'Team',
    icon: 'groups',
    route: '/settings/personnel/team',
    group: 'People',
    description: 'Workforce roster for this company',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerPersonnelTeam',
    searchKeywords: ['team', 'roster', 'workforce', 'staff', 'employees', 'people'],
  },
  {
    id: 'email-groups',
    favoriteId: 'company-email-groups',
    label: 'Email Groups',
    icon: 'alternate_email',
    route: '/settings/email-groups',
    group: 'People',
    description: 'Address lists you can email',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerEmailGroups',
    searchKeywords: ['email', 'groups', 'addresses', 'recipients', 'audience', 'distribution', 'list', 'mailing'],
  },
  {
    id: 'org-charts',
    favoriteId: 'company-personnel-org-charts',
    label: 'Org Charts',
    icon: 'schema',
    route: '/settings/personnel/org-charts',
    group: 'People',
    description: 'Reporting structure, visualized',
    permissions: [Permission.ManageCompanySettings],
    feature: 'employerOrgCharts',
    searchKeywords: ['org', 'chart', 'charts', 'reporting', 'structure', 'hierarchy', 'tree'],
  },
  {
    id: 'customer-clients',
    favoriteId: 'client-management',
    label: 'Client Management',
    icon: 'manage_accounts',
    route: '/customer-clients',
    group: 'People',
    description: 'Accounts you recruit for',
    permissions: [CustomerPermissions.ViewClient],
    searchKeywords: ['clients', 'customers', 'accounts', 'management'],
  },
]
